import { useCallback } from "react"
import { batch, useSignal } from "@preact/signals-react"
import { getSubmit, postSubmit } from "@ui-seeker/utils"

import { useToast } from "@hooks"
import { alertsDrawerData, clearSignals, loadingEditRowId, loadingRowId } from "Pages/ReportsAlerts/Alerts/Conditions/@signals"
import { alertsSection } from "Pages/ReportsAlerts/Alerts/@signals"


export default function useAlertsTableData(url = null) {

	const { showToast } = useToast()
	const tableData = useSignal([])
	const conditionFilters = useSignal([])
	const backupTableData = useSignal([])
	const isTableLoading = useSignal(false)
	const isAlertSubmitting = useSignal(false)

	const onGenerateAlert = useCallback(alertData => ({
		idAlert: null,
		conditionName: null,
		idMainSite: null,
		mainProvider: [],
		comparator: 0,
		comparatorValue: 1,
		comparatorUnit: 0,
		sites: [],
		sameSites: false,
		providers: [],
		sameProviders: false,
		excludeProviders: false,
		comparisonType: 0,
		acriss: [],
		category: [],
		mileage: null,
		transmission: null,
		fuelPolicy: null,
		airportLocation: null,
		sameInsurance: false,
		mainTiers: [],
		providersByTiers: [],
		notify: false,
		comparatorDiff: 0,
		positionPrice: null,
		comparatorPrices: 0,
		...alertData
	}), [url])

	const onEditAlert = useCallback((id) => {
		loadingEditRowId.value = id
		return getSubmit(`${url}/list?id=${id}`).then(data => {
			alertsDrawerData.value = { ...data, type: alertsSection.value, edit: true }
			loadingEditRowId.value = null
		}).catch(() => {
			loadingEditRowId.value = null
			showToast.error()
		})
	}, [])


	const getTableData = useCallback((urlProps = null) => {
		isTableLoading.value = true
		return getSubmit(`${url}/list`, urlProps).then(data => {
			batch(() => {
				backupTableData.value = data
				tableData.value = data
				isTableLoading.value = false
			})
			return data
		}).catch(() => batch(() => {
			tableData.value = []
			isTableLoading.value = false
		}))
	}, [url])

	const onSubmitAlert = useCallback(alertData => {
		isAlertSubmitting.value = true
		postSubmit(`${url}/${alertData?.edit ? 'edit' : 'create'}`, alertData).then(res => {
			clearSignals()
			setTableData(res)
			isAlertSubmitting.value = false
		}).catch(() => {
			showToast.error("Error creating alert")
			isAlertSubmitting.value = false
			clearSignals()
		})
	}, [url])

	const setTableData = useCallback(newData => {
		let targetIdx = null
		const allAlerts = tableData.value.map(item => item)
		const selectedAlert = allAlerts.filter((item, idx) => {
			const same = item.idAlert === newData?.idAlert
			if (same) targetIdx = idx
			return same
		})[0]

		if (selectedAlert) {
			allAlerts[targetIdx] = { ...selectedAlert, ...{ ...newData, justCreated: true } }
		} else {
			allAlerts.unshift({ ...newData, justCreated: true })
		}
		tableData.value = allAlerts
		backupTableData.value = allAlerts
	}, [url])

	const onDeleteAlert = useCallback(alertId => showToast.confirm('Are you sure you want to delete the alert? This action cannot be undone.', {
		onSubmit: () => {
			loadingRowId.value = alertId
			showToast.promise(postSubmit(`${url}/delete`, { id: alertId }), {
				onSuccess: () => {
					tableData.value = tableData.value.filter(item => item.idAlert !== alertId)
					backupTableData.value = [...tableData.value]
					loadingRowId.value = null
				}, onError: () => {
					loadingRowId.value = null
				}
			})
		}
	}), [url])

	const filterConditions = (value) => {
		if (!value.length) {
			tableData.value = [...backupTableData.value]
			return
		}

		if (conditionFilters.value.includes(value)) {
			conditionFilters.value = [...conditionFilters.value.filter(filter => filter !== value)]
		} else {
			conditionFilters.value = [...conditionFilters.value, value]
		}

		tableData.value = [...backupTableData.value.filter(
			({ comparatorName, comparatorPricesName, comparatorUnitName }) => {
				return conditionFilters.value.every((filter) => {
					return (comparatorName && filter === comparatorName) ||
						(comparatorPricesName && filter === comparatorPricesName) ||
						(comparatorUnitName && filter === comparatorUnitName)
				})
			}
		)]
	}


	return {
		filterConditions: filterConditions,
		getTableData,
		onGenerateAlert,
		onEditAlert,
		onSubmitAlert,
		onDeleteAlert,
		tableData: tableData.value,
		isTableLoading: isTableLoading.value,
		isAlertSubmitting: isAlertSubmitting.value,
	}
}